import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLibrary, useLibraryStats } from '@/hooks/useLibraries';
import { useEntries } from '@/hooks/useEntries';
import { useStreamLibrarySuggestions } from '@/hooks/useLibraryReview';
import {
  useLibraryAliases,
  useCreateLibraryAlias,
  useUpdateLibraryAlias,
  useDeleteLibraryAlias,
} from '@/hooks/useLibraryAliases';
import {
  useLibrarySections,
  useCreateLibrarySection,
  useUpdateLibrarySection,
  useDeleteLibrarySection,
} from '@/hooks/useLibrarySections';
import { LibrarySuggestionResponse, EntrySuggestion, VerifiedEntry } from '@/types/api';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorAlert from '@/components/common/ErrorAlert';
import MissingEntryDialog from '@/components/entries/MissingEntryDialog';
import AliasList from '@/components/libraries/AliasList';
import SectionEditor from '@/components/libraries/SectionEditor';
import EntryGrid from '@/components/libraries/EntryGrid';
import EntryEditDialog from '@/components/libraries/EntryEditDialog';
import ContextPanel from '@/components/messages/ContextPanel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const LibraryReviewPage: React.FC = () => {
  const { libraryId } = useParams<{ libraryId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('pages');
  const [currentTab, setCurrentTab] = useState(0);
  const [suggestions, setSuggestions] = useState<LibrarySuggestionResponse | null>(null);
  const [missingDialogOpen, setMissingDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<EntrySuggestion | null>(null);
  const [contextPanelOpen, setContextPanelOpen] = useState(false);
  const [contextMessageId, setContextMessageId] = useState<number | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<VerifiedEntry | null>(null);

  const libId = parseInt(libraryId || '0', 10);

  // Fetch library data
  const { data: library, isLoading: libraryLoading, isError: libraryError } = useLibrary(libId);
  const { data: stats } = useLibraryStats(libId);
  const { data: entries = [], refetch: refetchEntries } = useEntries({ library_id: libId });

  // Alias management
  const { data: aliases = [] } = useLibraryAliases(libId);
  const createAlias = useCreateLibraryAlias();
  const updateAlias = useUpdateLibraryAlias();
  const deleteAlias = useDeleteLibraryAlias();

  // Section management
  const { data: sections = [] } = useLibrarySections(libId);
  const createSection = useCreateLibrarySection();
  const updateSection = useUpdateLibrarySection();
  const deleteSection = useDeleteLibrarySection();

  // Streaming suggestions
  const {
    isStreaming,
    progress,
    suggestions: streamedSuggestions,
    stats: streamedStats,
    error: streamError,
    sectionsWithErrors,
    startStreaming,
  } = useStreamLibrarySuggestions(libId);

  const handleGenerateSuggestions = async () => {
    setSuggestions(null); // Reset old suggestions
    await startStreaming({
      min_confidence: 0.5,
      include_existing: true,
    });
  };

  // Update suggestions state when streaming completes
  React.useEffect(() => {
    if (!isStreaming && streamedSuggestions.length > 0) {
      setSuggestions({
        library_id: libId,
        library_name: library?.canonical_name || '',
        suggestions: streamedSuggestions,
        stats: streamedStats || {
          total_suggestions: 0,
          existing_entries: 0,
          new_suggestions: 0,
          avg_confidence: 0,
        },
      });
    }
  }, [isStreaming, streamedSuggestions, streamedStats, libId, library?.canonical_name]);

  const handleMarkAsMissing = (suggestion?: EntrySuggestion) => {
    setSelectedSuggestion(suggestion || null);
    setMissingDialogOpen(true);
  };

  const handleCreateMissingEntry = () => {
    setMissingDialogOpen(false);
    setSelectedSuggestion(null);
    void refetchEntries();
  };

  const handleEditEntry = (entry: VerifiedEntry) => {
    setEditingEntry(entry);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingEntry(null);
    void refetchEntries();
  };

  const handleViewEntryContext = (messageId: number) => {
    setContextMessageId(messageId);
    setContextPanelOpen(true);
  };

  const contextChatId = React.useMemo(() => {
    if (entries.length === 0) {
      return null;
    }
    if (contextMessageId === null) {
      return entries[0].chat_id;
    }

    const match = entries.find((entry) => entry.title_message_id === contextMessageId);
    return match?.chat_id ?? entries[0].chat_id;
  }, [entries, contextMessageId]);

  const handleEntryCreated = () => {
    void refetchEntries();
  };

  if (libraryLoading) {
    return <LoadingSpinner message={t('libraryReview.loading')} />;
  }

  if (libraryError || !library) {
    return <ErrorAlert error={new Error(t('libraryReview.errors.notFound'))} />;
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Box
        sx={{
          flex: contextPanelOpen ? '0 0 calc(100% - 370px)' : 1,
          overflowY: 'auto',
          p: 3,
          transition: 'flex 0.3s ease',
        }}
      >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/libraries')}
          >
            {t('libraryReview.actions.back')}
          </Button>
          <Typography variant="h4" sx={{ direction: 'rtl', textAlign: 'right' }}>
            {library.canonical_name}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleGenerateSuggestions}
          disabled={isStreaming}
        >
          {isStreaming
            ? t('libraryReview.actions.generating')
            : t('libraryReview.actions.generateSuggestions')}
        </Button>
      </Box>

      {/* Progress Indicator */}
      {isStreaming && progress && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {progress.message || `Processing ${progress.processed} of ${progress.total} sections...`}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress.percentage} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {progress.processed} / {progress.total}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {progress.percentage.toFixed(1)}%
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Error Alert */}
      {streamError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {streamError}
          {sectionsWithErrors.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption">
                Failed sections: {sectionsWithErrors.map(e => e.section_id).join(', ')}
              </Typography>
            </Box>
          )}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {t('libraryReview.stats.totalEntries')}
                </Typography>
                <Typography variant="h4">{stats.total_entries}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {t('libraryReview.stats.sections')}
                </Typography>
                <Typography variant="h4">{stats.section_count}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {t('libraryReview.stats.suggestions')}
                </Typography>
                <Typography variant="h4">
                  {isStreaming
                    ? streamedSuggestions.filter(s => !s.is_indexed).length
                    : suggestions?.stats.new_suggestions || 0}
                </Typography>
                {isStreaming && (
                  <Typography variant="caption" color="primary">
                    Generating...
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {t('libraryReview.stats.missing')}
                </Typography>
                <Typography variant="h4">
                  {entries.filter((e) => e.is_missing).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label={t('libraryReview.tabs.suggestions')} />
          <Tab label={t('libraryReview.tabs.entries')} />
          <Tab label={t('libraryReview.tabs.settings')} />
        </Tabs>

        {/* Tab 1: Suggestions */}
        <TabPanel value={currentTab} index={0}>
          {!suggestions && !isStreaming && (
            <Alert severity="info">
              {t('libraryReview.noSuggestions')}
            </Alert>
          )}

          {isStreaming && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Generating suggestions... ({streamedSuggestions.length} found so far)
            </Alert>
          )}

          {(suggestions || (isStreaming && streamedSuggestions.length > 0)) && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {suggestions
                  ? t('libraryReview.suggestionsTitle', {
                      total: suggestions.stats.total_suggestions,
                      existing: suggestions.stats.existing_entries,
                      new: suggestions.stats.new_suggestions,
                    })
                  : `Suggestions (${streamedSuggestions.length})`}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(suggestions?.suggestions || streamedSuggestions).map((suggestion) => (
                  <Card
                    key={suggestion.title_message_id}
                    sx={{
                      border: suggestion.is_indexed ? 2 : 1,
                      borderColor: suggestion.is_indexed ? 'success.main' : 'divider',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ direction: 'rtl', textAlign: 'right' }}>
                          {suggestion.title || t('libraryReview.untitled')}
                        </Typography>
                        {suggestion.is_indexed && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label={t('libraryReview.alreadyIndexed')}
                            color="success"
                            size="small"
                          />
                        )}
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        {t('libraryReview.author')}: {suggestion.author || t('libraryReview.unknown')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('libraryReview.msNo')}: {suggestion.ms_no || t('libraryReview.none')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('libraryReview.mediaCount')}: {suggestion.media_ids.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('libraryReview.confidence')}: {(suggestion.confidence * 100).toFixed(0)}%
                      </Typography>

                      {!suggestion.is_indexed && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleMarkAsMissing(suggestion)}
                          >
                            {t('libraryReview.actions.markAsMissing')}
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* Tab 2: Existing Entries */}
        <TabPanel value={currentTab} index={1}>
          <EntryGrid
            entries={entries}
            chatId={contextChatId || entries[0]?.chat_id || 0}
            libraryId={libId}
            onEdit={handleEditEntry}
            onViewContext={handleViewEntryContext}
            onEntryCreated={handleEntryCreated}
          />
        </TabPanel>

        {/* Tab 3: Library Settings */}
        <TabPanel value={currentTab} index={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('libraryReview.aliasesTitle')}
            </Typography>
            <AliasList
              open={true}
              libraryName={library.canonical_name}
              aliases={aliases}
              onClose={() => {}}
              onAddAlias={async (data) => {
                await createAlias.mutateAsync({ libraryId: libId, data });
              }}
              onUpdateAlias={async (aliasId, data) => {
                await updateAlias.mutateAsync({ libraryId: libId, aliasId, data });
              }}
              onDeleteAlias={async (aliasId) => {
                await deleteAlias.mutateAsync({ libraryId: libId, aliasId });
              }}
              isLoading={
                createAlias.isPending ||
                updateAlias.isPending ||
                deleteAlias.isPending
              }
            />

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              {t('libraryReview.sectionsTitle')}
            </Typography>
            <SectionEditor
              open={true}
              libraryName={library.canonical_name}
              sections={sections}
              onClose={() => {}}
              onAddSection={async (data) => {
                await createSection.mutateAsync({ libraryId: libId, data });
              }}
              onUpdateSection={async (sectionId, data) => {
                await updateSection.mutateAsync({ libraryId: libId, sectionId, data });
              }}
              onDeleteSection={async (sectionId) => {
                await deleteSection.mutateAsync({ libraryId: libId, sectionId });
              }}
              isLoading={
                createSection.isPending ||
                updateSection.isPending ||
                deleteSection.isPending
              }
            />
          </Box>
        </TabPanel>
      </Paper>

      <EntryEditDialog
        open={editDialogOpen}
        entry={editingEntry}
        onClose={handleCloseEditDialog}
      />

      {/* Missing Entry Dialog */}
      <MissingEntryDialog
        open={missingDialogOpen}
        libraryId={libId}
        existingEntries={entries}
        mode="create"
        onClose={() => {
          setMissingDialogOpen(false);
          setSelectedSuggestion(null);
        }}
        onCreated={handleCreateMissingEntry}
        initialMsNo={selectedSuggestion?.ms_no || ''}
        initialTitle={selectedSuggestion?.title || ''}
        initialAuthor={selectedSuggestion?.author || ''}
      />
      </Box>

      {contextPanelOpen && (
        <ContextPanel
          open={contextPanelOpen}
          chatId={contextChatId}
          anchorId={contextMessageId}
          suggestions={[]}
          onClose={() => setContextPanelOpen(false)}
          onJumpToId={(messageId) => setContextMessageId(messageId)}
        />
      )}
    </Box>
  );
};

export default LibraryReviewPage;
